import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Save, RefreshCw, Flame, Gift, Compass, Crown, Award, Trophy } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/Avatar";

const Stat = ({ icon: Icon, label, value }) => (
  <div className="bg-card border border-border rounded-2xl p-5 flex items-center gap-4">
    <div className="w-11 h-11 rounded-full bg-primary/15 flex items-center justify-center text-primary"><Icon className="w-5 h-5" /></div>
    <div><p className="text-2xl font-heading font-semibold">{value}</p><p className="text-sm text-muted-foreground">{label}</p></div>
  </div>
);

const Profile = () => {
  const { user, refreshUser, isPremium } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [bio, setBio] = useState(user?.bio || "");
  const [soberDate, setSoberDate] = useState((user?.sober_date || "").slice(0, 10));
  const [saving, setSaving] = useState(false);
  const [regen, setRegen] = useState(false);
  const [game, setGame] = useState(null);

  useEffect(() => {
    (async () => { try { const { data } = await api.get("/gamification/profile"); setGame(data); } catch (e) {} })();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await api.patch("/profile", { name, bio, sober_date: soberDate }); await refreshUser(); toast.success("Profile updated"); }
    catch (err) { toast.error("Could not update profile"); }
    finally { setSaving(false); }
  };

  const regenAlias = async () => {
    setRegen(true);
    try { await api.post("/profile/alias/generate"); await refreshUser(); toast.success("New alias generated"); }
    catch (e) { toast.error("Could not regenerate alias"); }
    finally { setRegen(false); }
  };

  const inp = "mt-1.5 w-full px-4 py-2.5 rounded-lg border border-input bg-secondary/40 outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary";
  const earned = (game?.badges_earned || []).length;

  return (
    <div className="px-4 sm:px-6 py-8">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <Avatar name={user?.name} src={user?.avatar_base64} size={72} testId="profile-avatar" />
          <div className="min-w-0">
            <h1 className="font-heading text-3xl font-semibold truncate">{user?.name}</h1>
            <p className="text-muted-foreground truncate">{user?.email}</p>
            {isPremium && <span className="inline-flex items-center gap-1.5 mt-1 text-xs text-brass font-medium"><Crown className="w-3.5 h-3.5" /> Premium member</span>}
          </div>
        </div>

        {game && (
          <div className="bg-card border border-border rounded-2xl p-5 mb-6" data-testid="gamification-card">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2"><Trophy className="w-5 h-5 text-brass" /><span className="font-semibold">Level {game.level} · {game.title}</span></div>
              <span className="text-sm text-muted-foreground">{game.total_xp} XP</span>
            </div>
            <div className="h-2 rounded-full bg-secondary overflow-hidden">
              <div className="h-full bg-brass" style={{ width: `${Math.min(100, game.progress || 0)}%` }} />
            </div>
            {game.next_level && <p className="text-xs text-muted-foreground mt-2">{game.next_level.xp_required} XP to level {game.next_level.level} · {game.next_level.title}</p>}
          </div>
        )}

        <div className="grid grid-cols-3 gap-4 mb-8">
          <Stat icon={Flame} label="Day streak" value={user?.streak ?? 0} />
          <Stat icon={Award} label="Badges" value={earned} />
          <Stat icon={Gift} label="Referrals" value={user?.referral_count ?? 0} />
        </div>

        <div className="bg-card border border-border rounded-2xl p-6 shadow-sm mb-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Compass className="w-5 h-5 text-brass" />
              <div><p className="text-sm text-muted-foreground">Community alias</p><p className="font-heading text-lg font-semibold" data-testid="profile-alias">{user?.display_alias || "—"}</p></div>
            </div>
            <button data-testid="regenerate-alias-button" onClick={regenAlias} disabled={regen}
              className="flex items-center gap-2 text-sm font-medium text-primary border border-border rounded-full px-4 py-2 hover:bg-secondary transition-colors disabled:opacity-50">
              {regen ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} New alias
            </button>
          </div>
          {user?.referral_code && <p className="text-sm text-muted-foreground mt-4">Referral code: <span className="font-mono font-semibold text-foreground" data-testid="referral-code">{user.referral_code}</span></p>}
        </div>

        <form onSubmit={save} className="bg-card border border-border rounded-2xl p-6 shadow-sm mb-6">
          <h2 className="font-heading text-xl font-semibold mb-5">Edit profile</h2>
          <div className="space-y-4">
            <div><label className="text-sm font-medium">Name</label><input data-testid="profile-name-input" value={name} onChange={(e) => setName(e.target.value)} className={inp} /></div>
            <div><label className="text-sm font-medium">Sober date</label><input data-testid="profile-sober-input" type="date" value={soberDate} onChange={(e) => setSoberDate(e.target.value)} className={inp} /></div>
            <div><label className="text-sm font-medium">Bio</label><textarea data-testid="profile-bio-input" rows={3} value={bio} onChange={(e) => setBio(e.target.value)} placeholder="A few words about your journey…" className={inp + " resize-none"} /></div>
          </div>
          <div className="flex justify-end mt-5">
            <button data-testid="profile-save-button" type="submit" disabled={saving}
              className="flex items-center gap-2 bg-primary text-primary-foreground text-sm font-semibold px-6 py-2.5 rounded-full hover:bg-atlantic active:scale-95 transition-all disabled:opacity-50">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save changes
            </button>
          </div>
        </form>

        <Link to="/upgrade" data-testid="manage-subscription" className="block bg-gradient-to-br from-brass/20 to-brass/5 border border-brass/40 rounded-2xl p-5 hover:from-brass/25 transition-colors">
          <div className="flex items-center gap-3"><Crown className="w-5 h-5 text-brass" /><div><p className="font-semibold">Manage subscription</p><p className="text-sm text-muted-foreground">View plans & Premium benefits</p></div></div>
        </Link>
      </div>
    </div>
  );
};

export default Profile;
